import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import MainLayout from '../components/MainLayout';
import requests from '../api/requests';
import { answerEndpoint, randomObservationEndpoint } from '../api/endpoints';
import { QuestionTypesContext } from '../context/QuestionTypesContext';
import ImageTile from '../components/ImageTile';
import Ratings from '../components/Ratings';
import { Collapse, Modal } from 'antd';
import { CookieHolder } from '../util/CookieHolder';
import InfoBanner from '../components/InfoBanner';
import emj1 from '../assets/emoji1.svg';
import emj2 from '../assets/emoji2.svg';
import emj3 from '../assets/emoji3.svg';
import emj4 from '../assets/emoji4.svg';
import emj5 from '../assets/emoji5.svg';
import { InfoModalContext } from '../context/InfoModalContext';


const MainPage = () => {
  const questionTypes = useContext(QuestionTypesContext)
  const {open: infoModalOpen, toggleModal} = useContext(InfoModalContext)
  const [ loading, setLoading ] = useState(true);
  const [ currentPhoto, setCurrentPhoto ] = useState(undefined);
  const [ consentModalVisible, setConsentModalVisible ] = useState(false);
  const [ answeredCount, setAnsweredCount ] = useState(+localStorage.getItem('answeredCount') || 0);
  const [ currentAnswers, setCurrentAnswers ] = useState({});
  const [ allAnswered, setAllAnswered ] = useState(false);
  const agreedToTerms = useRef(localStorage.getItem('consent') === 'true');

  const currentObservation = useRef(null);

  const emojiData = useMemo(() => {
    if (answeredCount >= 50) {
      return {
        textPrimary: 'Amazing!',
        textSecondary: 'Are you enjoying our animal collection?',
        emoji: emj5
      }
    }
    if (answeredCount >= 30) {
      return {
        textPrimary: 'Great!',
        textSecondary: 'Your contribution will surely make a difference!',
        emoji: emj4
      }
    }
    if (answeredCount >= 15) {
      return {
        textPrimary: 'Very good!',
        textSecondary: 'You have covered a considerable amount of species groups!',
        emoji: emj3
      }
    }
    if (answeredCount >= 5) {
      return {
        textPrimary: 'Good!',
        textSecondary: 'You have seen a few of our spectacular species!',
        emoji: emj2
      }
    }
    if (answeredCount > 0) {
      return {
        textPrimary: 'Nice!',
        textSecondary: 'Let’s share more of your feelings for other species!',
        emoji: emj1
      }
    }

    return null;
  }, [ answeredCount ])

  const consent = () => {
    localStorage.setItem('consent', 'true');
    agreedToTerms.current = true;
    closeModal();
    submitAnswers(currentAnswers)
  }

  const closeModal = () => {
    setConsentModalVisible(false);
  }

  useEffect(() => {
    if (answeredCount) {
      localStorage.setItem('answeredCount', answeredCount)
    }
  }, [ answeredCount ])

  useEffect(() => {
    setAllAnswered(Array.isArray(questionTypes) && !!questionTypes.length && Object.keys(currentAnswers).length === questionTypes.length)
  }, [ currentAnswers, questionTypes ])

  const submitAnswers = useCallback((answers) => {
    const observationId = currentObservation.current.id;
    const data = Object.entries(answers).map(([ questionType, value ]) => ({
      observationId,
      questionType,
      value
    }))
    setLoading(true);
    requests.post(answerEndpoint(), data, {
      'x-wasp-cookie': CookieHolder.uuidCookie
    })
      .then(response => {
        if (response) {
          setAnsweredCount((prev) => prev + 1)
          const observation = response.data;
          currentObservation.current = observation;
          if (observation) {
            const photo = {
              src: requests.resolveIMagePath(observation.medium?.id),
              photographer: observation.photoAttribution,
              name: observation.name
            }
            setCurrentPhoto(photo)
          }
        }
      })
      .finally(() => {
        setCurrentAnswers({});
        setLoading(false)
      })

  }, [ ])

  useEffect(() => {
    setLoading(true);
    if (CookieHolder.uuidCookie === '') {
      CookieHolder.initialize();
    }
    requests.get(randomObservationEndpoint(), {
      'x-wasp-cookie': CookieHolder.uuidCookie
    })
      .then((response) => {
        if (response) {
          currentObservation.current = response.data
          setCurrentPhoto({
            ...response.data,
            src: requests.resolveIMagePath(response.data?.medium?.id),
            photographer: response.data?.photoAttribution
          })

        }
        if (CookieHolder.uuidCookie === '') {
          CookieHolder.initialize();
        }
      })
      .finally(() => {
        setLoading(false)
      })

  },  [])

  const onRate = () => {
    if (agreedToTerms.current) {
      submitAnswers(currentAnswers);
    }
    else {
      setConsentModalVisible(true);
    }
  }

  return (
    <MainLayout loading={loading}>
      <div className="main-page">
        <div className="image-container">
          <div className="image-tile-container mobile-only">
            {currentPhoto && (
              <>
                <ImageTile
                  imageUrl={currentPhoto.src}
                  photographer={currentPhoto.photographer}
                  speciesName={currentPhoto.name}
                />
                <Ratings
                  types={questionTypes}
                  values={currentAnswers}
                  onChange={setCurrentAnswers}
                  viewport="mobile"
                  onSubmit={onRate}
                  submitDisabled={!allAnswered}
                />
              </>
            )}
          </div>
          <div className="image-tile-container desktop-only">
            {currentPhoto && (
              <>
                <ImageTile
                  imageUrl={currentPhoto.src}
                  photographer={currentPhoto.photographer}
                  speciesName={currentPhoto.name}
                />
                <Ratings
                  types={questionTypes}
                  values={currentAnswers}
                  onChange={setCurrentAnswers}
                  viewport="desktop"
                  onSubmit={onRate}
                  submitDisabled={!allAnswered}
                />
              </>
            )}
          </div>
        </div>
      </div>
      <Modal
        title="Consent form"
        visible={consentModalVisible}
        onOk={consent}
        onCancel={closeModal}
        okText="I agree to all information above" closable={false}
      >
        <p>
          To participate in the survey, please read the information below thoroughly:
          <ul>
            <li>I understand that this survey collects my perception for different species, the results of which may be
              used purely for scientific purposes and may be published.
            </li>
            <li>I understand that my participation in this survey is voluntary and that I have the right to discontinue
              my
              participation at any time during the collection (by closing the browser window).
            </li>
            <li> I understand that my general geolocation (country-level) may be collected, but I will remain anonymous
              at every stage of the research.
            </li>
            <li>If I have any questions, I know I can contact Tuan Nguyen (tuan.nguyen@uhasselt.be).</li>
            <li>I have read and understood the above information.</li>
            <li>I agree to participate in this study.</li>
          </ul>
        </p>
      </Modal>
      <Modal
        title=""
        visible={infoModalOpen}
        onOk={() => toggleModal(false)}
        onCancel={() => toggleModal(false)}
        cancelButtonProps={{style:{display: 'none'}}}
      >
        <Collapse ghost activeKey={answeredCount > 0 ? [ 'ratedCount' ] : undefined}>
          <Collapse.Panel key="ratedCount" header="" showArrow={false}>
            <div className="rated-count">
              <span>
                Number of species rated: ({answeredCount})
              </span>
              {!!emojiData && (
                <div className="emj-wrapper">
                  <div className="emj-img"><img src={emojiData.emoji} alt="" /></div>
                  <div className="emj-text">
                    <div>{emojiData.textPrimary}</div>
                    <div>{emojiData.textSecondary}</div>
                  </div>
                </div>
              )}
            </div>
          </Collapse.Panel>
        </Collapse>
        <InfoBanner />
      </Modal>
    </MainLayout>
  )
};

export default MainPage;
